import { TMasterStandardIndustryGroup } from "@/api/entities/master-standard-industry-group.entity";
import { useCreateStandardIndustryGroup, useUpdateStandardIndustryGroup } from "@/api/services/global/master";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { DataConflictModal } from "@/components/commons/DataConflictModal";
import { useDialog } from "@/hooks/useDialog";
import { useStandardIndustryConflictModal } from "@/features/master-data/hooks/useStandardIndustryConflictModal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { standardIndustryGroupSchema } from "@/features/master-data/schema/masterDataStandardIndustrySchema";
import { TStandardIndustryGroupForm } from "@/features/master-data/types/masterDataStandardIndustryForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { FC } from "react";
import { useForm } from "react-hook-form";

type TGroupFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedGroup?: TMasterStandardIndustryGroup;
};

export const GroupFormModal: FC<TGroupFormModalProps> = ({ isOpen, onClose, selectedGroup }) => {
  const createMutation = useCreateStandardIndustryGroup();
  const updateMutation = useUpdateStandardIndustryGroup();
  const isEdit = Boolean(selectedGroup);

  const methods = useForm<TStandardIndustryGroupForm>({
    resolver: yupResolver(standardIndustryGroupSchema),
    defaultValues: { code: selectedGroup?.code ?? "", name: selectedGroup?.name ?? "" },
    mode: "all",
    reValidateMode: "onBlur",
  });

  const {
    control,
    setValue,
    formState: { isDirty },
  } = methods;

  const confirmClose = useDialog();
  const conflict = useStandardIndustryConflictModal(onClose);

  const handleRequestClose = async () => {
    if (!isDirty) {
      onClose();
      return;
    }
    const isConfirmed = await confirmClose.show();
    if (isConfirmed) {
      onClose();
    }
  };

  const onSubmit = async (data: TStandardIndustryGroupForm) => {
    try {
      const rs = selectedGroup
        ? await updateMutation.mutateAsync({
            id: toNumber(selectedGroup.id),
            payload: { code: data.code, name: data.name, updatedAt: selectedGroup.updatedAt },
          })
        : await createMutation.mutateAsync({ code: data.code, name: data.name });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      onClose();
    } catch (error) {
      if (conflict.handleConflictError(error)) {
        return;
      }
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal
      width={600}
      title={isEdit ? "大分類の編集" : "大分類の追加"}
      isOpen={isOpen}
      enableFooter={false}
      onClose={handleRequestClose}
      enableCloseIcon={false}
    >
      <Form methods={methods} onSubmit={onSubmit} schema={standardIndustryGroupSchema}>
        <Grid container spacing={3} py={3}>
          <Grid item xs={12}>
            <FormField control={control} name="code">
              <Input
                sx={{ "& input": { textTransform: "uppercase" } }}
                onBlur={(e) => setValue("code", e.target.value.toUpperCase(), { shouldDirty: true, shouldValidate: true })}
              />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="name">
              <Input />
            </FormField>
          </Grid>
        </Grid>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={handleRequestClose}>
            キャンセル
          </Button>
          <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
            保存
          </Button>
        </Stack>
      </Form>
      <ConfirmSaveModal open={confirmClose.open} onOk={confirmClose.confirm} onClose={confirmClose.cancel}>
        <Typography>入力したデータがなくなりますが、よろしいですか。</Typography>
      </ConfirmSaveModal>
      <DataConflictModal
        open={conflict.isConflictOpen}
        name={conflict.conflictName}
        onConfirm={conflict.confirmReload}
        onCancel={conflict.cancelConflict}
      />
    </Modal>
  );
};
